/* ── Card Signature Effect base class ─────────────────────────────
 * Shared scaffolding for the per-card-cast VFX siblings (GuardianTauntAura,
 * HealerBeaconRing, InterceptorDashTrail, SniperReticleLaser, SupportBuffAura).
 *
 * Each concrete effect previously duplicated:
 *   - private life = 0; private disposed = false;
 *   - update(deltaMs): increment life, advance progress, dispose at expiry
 *   - dispose(): guard double-dispose, scene.remove, dispose resources
 *
 * The shape that varies is the per-frame uniform/transform animation and
 * the resource-disposal list. Subclasses implement those via `tick(t, dtSec)`
 * and `disposeResources()`.
 *
 * Renderer convention (preserved):
 *   - constructor builds geometry/material and adds the root to scene
 *   - update(deltaMs) returns false ONLY at end-of-life or after dispose
 *   - dispose() is idempotent and frees GPU resources
 * ───────────────────────────────────────────────────────────── */

import type * as THREE from 'three/webgpu';

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/** Optional callback that returns the caster's current live position each
 * tick. Signatures use this to follow a moving caster instead of staying
 * pinned to the position captured at spawn. Returning null leaves the
 * signature anchored to its last known position. */
export type LivePositionResolver = () => Vec3Like | null;

export abstract class CardSignatureEffect {
  protected readonly scene: THREE.Scene;
  protected life = 0;
  protected disposed = false;
  protected readonly lifeSeconds: number;

  protected constructor(scene: THREE.Scene, lifeSeconds: number) {
    this.scene = scene;
    this.lifeSeconds = lifeSeconds;
  }

  /** Returns true while still alive; false on the tick that disposes. */
  update(deltaMs: number): boolean {
    if (this.disposed) return false;
    this.life += deltaMs / 1000;
    const t = this.life / this.lifeSeconds;
    if (t >= 1) {
      this.dispose();
      return false;
    }
    this.tick(t, deltaMs / 1000);
    return true;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.disposeResources();
  }

  /** Advance per-frame animation (uniforms, transforms). `t` ∈ [0, 1). */
  protected abstract tick(t: number, dtSec: number): void;

  /** Remove the root from the scene and dispose all geometry/material handles. */
  protected abstract disposeResources(): void;

  /**
   * Move `target.position` to track a caster's live centroid each tick.
   * Returning null from the resolver leaves the target anchored to its
   * last known position. Used by signatures whose entire group repositions
   * to follow the caster (Guardian aura, Healer beacon, Orbit ring, etc.).
   */
  protected applyLiveAnchor(
    target: { position: { set(x: number, y: number, z: number): unknown } },
    resolver: LivePositionResolver | undefined,
    yOffset = 0,
  ): void {
    if (!resolver) return;
    const live = resolver();
    if (!live) return;
    target.position.set(live.x, live.y + yOffset, live.z);
  }

  /**
   * Translate `target.position` by the delta between the resolver's current
   * value and an `initial` reference point. Used by signatures whose mesh
   * geometry was baked from positions captured at spawn (Interceptor dash
   * curve, Support per-ally rings, BR scatter splash).
   */
  protected applyLiveDelta(
    target: { position: { set(x: number, y: number, z: number): unknown } },
    resolver: LivePositionResolver | undefined,
    initial: Vec3Like,
  ): void {
    if (!resolver) return;
    const live = resolver();
    if (!live) return;
    target.position.set(live.x - initial.x, live.y - initial.y, live.z - initial.z);
  }
}
